import { StyledSelect } from "../../primitives/styled-select";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AlertTriangle, Banknote, Landmark, Zap } from "lucide-react";
import type * as React from "react";
import { cn } from "../../lib/cn";
import { Checkbox } from "../../primitives/checkbox";
import type { ActionFieldError, ActionFieldSpec, ActionFieldValue } from "../ActionFieldEditor";
import { VendorPreviewField, VendorPreviewShell } from "./VendorPreviewShell";
import { ENVELOPE_CLASSES, asString, specOfName, useEnvelopeCommit } from "./envelope-primitives";

/**
 * SepaPaymentPreview — controlled HITL editor for ``initiate_sepa_payment``
 * proposals (dispose a SEPA Credit Transfer via open banking — June 2026).
 *
 * This is the HIGHEST-RISK action surface in the suite: confirming moves
 * real money and is NOT reversible by Ando (BE marks it ``RiskLevel.CRITICAL``
 * + ``reversible=False``; ``FocusedActionView`` additionally gates execute
 * behind a typed-confirm). The UI therefore leads with the two values a
 * human must triple-check before disposing: the AMOUNT and the BENEFICIARY
 * (name + IBAN), framed by an explicit "stai per spostare denaro" warning.
 *
 * Layout (top → bottom), highest-risk first:
 *   1. Warning eyebrow — "Stai per disporre un bonifico" (irreversible).
 *   2. Amount hero — large tabular-nums € figure (the dominant element),
 *      editable inline.
 *   3. Beneficiary block — ``to_name`` + ``to_iban`` (monospace/uppercase,
 *      IBAN format + live validation surfaced from the schema errors).
 *   4. Debtor account (``from_account_id``) — SELECT when the open-banking
 *      connection exposes accounts, free-text fallback otherwise.
 *   5. Causale (``description``) + Bonifico istantaneo (``instant`` toggle).
 *
 * Mirrors the controlled render-prop contract of :class:`EmailSendDraftPreview`
 * / :class:`SendMessagePreview`: the parent (``ActionDrawer`` via
 * ``pickPreview``) hands ``schema`` / ``values`` / ``errors`` / ``onChange``
 * / ``disabled`` so every input binds to the drawer's LIVE values. Fields
 * listed in :data:`SEPA_PREVIEW_OWNED_FIELDS` are filtered out of the stacked
 * ``ActionFieldEditor`` (via ``previewOwnedFields``) so the operator never
 * sees the same input twice. Without the controlled API it degrades to a
 * read-only snapshot (post-execution / ``executed`` / ``failed`` states),
 * exactly like the email/message envelopes.
 *
 * Field keys match the BE wire (``InitiateSepaPaymentPreview`` +
 * ``_synth_initiate_sepa_payment_fields``): ``from_account_id`` / ``to_iban``
 * / ``to_name`` / ``amount`` / ``currency`` / ``description`` / ``instant``.
 */

export const SEPA_PREVIEW_OWNED_FIELDS = [
  "from_account_id",
  "to_iban",
  "to_name",
  "amount",
  "currency",
  "description",
  "instant",
] as const;

type SepaFieldName = (typeof SEPA_PREVIEW_OWNED_FIELDS)[number];

export interface SepaPaymentPreviewProps {
  /** Live field values (proposed merged with edits). */
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  /** When provided (post-execution), draws the executed copy read-only. */
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Controlled API — present when the proposal is still editable. */
  readonly schema?: readonly ActionFieldSpec[];
  readonly values?: Readonly<Record<string, ActionFieldValue>>;
  readonly errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  readonly onChange?: (next: Record<string, ActionFieldValue>) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Coerce an ``amount`` wire value (Decimal-safe string) to a finite number. */
function asAmountNumber(v: ActionFieldValue | undefined): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    // Accept both ``1234.56`` (wire) and ``1.234,56`` (italian-typed) shapes.
    const normalised =
      v.includes(",") && !v.includes(".")
        ? v.replace(/\./g, "").replace(",", ".")
        : v.replace(/(?<=\d)\.(?=\d{3}\b)/g, "");
    const n = Number(normalised);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const currencyFmt = (code: string) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: code || "EUR",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

/** Group an IBAN into 4-char blocks for legibility (display-only). */
function groupIban(raw: string): string {
  const clean = raw.replace(/\s+/g, "").toUpperCase();
  return clean.replace(/(.{4})/g, "$1 ").trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SepaPaymentPreview: React.FC<SepaPaymentPreviewProps> = ({
  proposedFields,
  executedFields,
  schema,
  values,
  errors,
  onChange,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const isControlled = Boolean(values && onChange);
  const display = executedFields ?? proposedFields;
  const liveValues = values ?? display;

  const fromSpec = specOfName(schema, "from_account_id");
  const ibanSpec = specOfName(schema, "to_iban");
  const nameSpec = specOfName(schema, "to_name");
  const amountSpec = specOfName(schema, "amount");
  const currencySpec = specOfName(schema, "currency");
  const descSpec = specOfName(schema, "description");
  const instantSpec = specOfName(schema, "instant");

  const commit = useEnvelopeCommit<SepaFieldName>(values, onChange);

  const currency = asString(liveValues.currency) || "EUR";
  const fromValue = asString(liveValues.from_account_id);
  const ibanValue = asString(liveValues.to_iban);
  const nameValue = asString(liveValues.to_name);
  const amountValue = asString(liveValues.amount);
  const descValue = asString(liveValues.description);
  const instantValue = Boolean(liveValues.instant);

  const amountNumber = asAmountNumber(liveValues.amount);
  const amountDisplay = amountNumber !== null ? currencyFmt(currency).format(amountNumber) : "—";

  // Resolve the human label for the chosen debtor account (the SELECT shows
  // it; the read-only snapshot needs it too).
  const fromOptions = fromSpec?.options ?? [];
  const fromLabel = fromOptions.find((o) => o.value === fromValue)?.label || fromValue;

  // -------------------------------------------------------------------------
  // Read-only fallback — post-execution snapshot (no editable inputs).
  // -------------------------------------------------------------------------
  if (!isControlled) {
    return (
      <VendorPreviewShell
        className={className}
        icon={<Banknote className="h-4 w-4" aria-hidden />}
        title={i18n._(msg`Bonifico SEPA`)}
        caption={nameValue || undefined}
        bottomBanner={{
          label: i18n._(msg`Importo`),
          value: <span className="text-[15px] tabular-nums">{amountDisplay}</span>,
          tone: "accent",
        }}
      >
        <VendorPreviewField
          label={i18n._(msg`Beneficiario`)}
          value={nameValue ? <span className="font-medium text-ink-1">{nameValue}</span> : null}
          hideEmpty
        />
        <VendorPreviewField
          label={i18n._(msg`IBAN`)}
          value={
            ibanValue ? <span className="font-mono uppercase">{groupIban(ibanValue)}</span> : null
          }
          hideEmpty
        />
        <VendorPreviewField label={i18n._(msg`Da conto`)} value={fromLabel || null} hideEmpty />
        <VendorPreviewField label={i18n._(msg`Causale`)} value={descValue || null} hideEmpty />
        {instantValue ? (
          <VendorPreviewField
            label={i18n._(msg`Tipo`)}
            value={i18n._(msg`Istantaneo (SCT Inst)`)}
          />
        ) : null}
      </VendorPreviewShell>
    );
  }

  // -------------------------------------------------------------------------
  // Controlled — money-mover editor. Amount + beneficiary dominate the top.
  // -------------------------------------------------------------------------
  const amountInvalid = Boolean(errors?.amount);
  const ibanInvalid = Boolean(errors?.to_iban);
  const currencyOptions = currencySpec?.options ?? [{ value: "EUR", label: "EUR" }];
  const showInstant = instantSpec !== undefined || instantValue;

  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      {/* (1) Irreversibility warning — non color-only (icon + text). Kept as a
          leading note above the sheet so it is read BEFORE any input. */}
      <div
        role="note"
        className="flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          <strong className="font-semibold">{i18n._(msg`Stai per spostare denaro.`)}</strong>{" "}
          {i18n._(msg`Un bonifico disposto`)} <strong>{i18n._(msg`non è reversibile`)}</strong>
          {i18n._(msg`: verifica importo, beneficiario e IBAN prima di confermare.`)}
        </span>
      </div>

      <VendorPreviewShell
        icon={<Banknote className="h-4 w-4" aria-hidden />}
        title={i18n._(msg`Bonifico SEPA`)}
        caption={nameValue || undefined}
      >
        {/* (2) Amount hero — the dominant value. */}
        <VendorPreviewField
          label={amountSpec?.required !== false ? i18n._(msg`Importo *`) : i18n._(msg`Importo`)}
          wide
          value={
            <div className="flex flex-col gap-1.5">
              <div
                className={cn(
                  "flex items-baseline gap-2 rounded-md border bg-transparent px-3 py-2 focus-within:ring-2 focus-within:ring-accent/30",
                  amountInvalid
                    ? "border-destructive focus-within:ring-destructive/30"
                    : "border-hairline focus-within:border-accent",
                )}
              >
                <span className="font-semibold text-2xl text-ink-2 tabular-nums" aria-hidden>
                  €
                </span>
                <input
                  id="sepa-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={0}
                  className="w-full bg-transparent font-semibold text-3xl text-ink-1 tabular-nums placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  value={amountValue}
                  onChange={(e) => commit("amount", e.target.value)}
                  disabled={disabled || amountSpec?.readonly}
                  placeholder={i18n._(msg`0,00`)}
                  aria-label={i18n._(msg`Importo del bonifico in euro`)}
                  aria-invalid={amountInvalid || undefined}
                />
                {/* (currency) — SELECT, EUR-only today but schema-driven. */}
                <StyledSelect
                  className="shrink-0 rounded border border-hairline bg-transparent px-1.5 py-1 text-ink-2 text-xs focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30 disabled:opacity-60"
                  value={currency}
                  onChange={(e) => commit("currency", e.target.value)}
                  disabled={disabled || currencySpec?.readonly}
                  aria-label={i18n._(msg`Valuta`)}
                >
                  {currencyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </StyledSelect>
              </div>
              {errors?.amount ? (
                <p className={ENVELOPE_CLASSES.err}>{errors.amount.message}</p>
              ) : amountNumber !== null ? (
                <p className={ENVELOPE_CLASSES.help}>
                  {currencyFmt(currency).format(amountNumber)}
                </p>
              ) : amountSpec?.helperText ? (
                <p className={ENVELOPE_CLASSES.help}>{amountSpec.helperText}</p>
              ) : null}
            </div>
          }
        />

        {/* (3a) Beneficiary — name. Prominence carried by the leading warning. */}
        <VendorPreviewField
          label={i18n._(msg`A`)}
          wide
          value={
            <div className="flex flex-col gap-1">
              <input
                type="text"
                className={cn(
                  ENVELOPE_CLASSES.input,
                  "font-medium",
                  errors?.to_name && "border-destructive focus:ring-destructive/30",
                )}
                value={nameValue}
                onChange={(e) => commit("to_name", e.target.value)}
                disabled={disabled || nameSpec?.readonly}
                placeholder={i18n._(msg`Nome del beneficiario`)}
                aria-label={i18n._(msg`Nome del beneficiario`)}
                aria-invalid={Boolean(errors?.to_name) || undefined}
              />
              {errors?.to_name ? (
                <p className={ENVELOPE_CLASSES.err}>{errors.to_name.message}</p>
              ) : nameSpec?.helperText ? (
                <p className={ENVELOPE_CLASSES.help}>{nameSpec.helperText}</p>
              ) : null}
            </div>
          }
        />

        {/* (3b) Beneficiary — IBAN. */}
        <VendorPreviewField
          label={i18n._(msg`IBAN`)}
          wide
          value={
            <div className="flex flex-col gap-1">
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                className={cn(
                  ENVELOPE_CLASSES.input,
                  "font-mono uppercase tracking-wider",
                  ibanInvalid && "border-destructive focus:ring-destructive/30",
                )}
                value={ibanValue}
                onChange={(e) => commit("to_iban", e.target.value.toUpperCase())}
                disabled={disabled || ibanSpec?.readonly}
                placeholder={i18n._(msg`IT60 X054 2811 1010 0000 0123 456`)}
                aria-label={i18n._(msg`IBAN del beneficiario`)}
                aria-invalid={ibanInvalid || undefined}
              />
              {errors?.to_iban ? (
                <p className={ENVELOPE_CLASSES.err}>{errors.to_iban.message}</p>
              ) : ibanSpec?.helperText ? (
                <p className={ENVELOPE_CLASSES.help}>{ibanSpec.helperText}</p>
              ) : null}
            </div>
          }
        />

        {/* (4) Debtor account. */}
        <VendorPreviewField
          label={i18n._(msg`Da conto`)}
          wide
          value={
            <div className="flex flex-col gap-1">
              {fromOptions.length > 0 ? (
                <span className="flex items-center gap-2">
                  <Landmark className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
                  <StyledSelect
                    className={ENVELOPE_CLASSES.input}
                    value={fromValue}
                    onChange={(e) => commit("from_account_id", e.target.value)}
                    disabled={disabled || fromSpec?.readonly}
                    aria-label={i18n._(msg`Conto di addebito`)}
                  >
                    {!fromSpec?.required ? <option value="">—</option> : null}
                    {fromOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </StyledSelect>
                </span>
              ) : (
                <input
                  type="text"
                  className={ENVELOPE_CLASSES.input}
                  value={fromValue}
                  onChange={(e) => commit("from_account_id", e.target.value)}
                  disabled={disabled || fromSpec?.readonly}
                  placeholder={i18n._(msg`Conto di addebito`)}
                  aria-label={i18n._(msg`Conto di addebito`)}
                />
              )}
              {errors?.from_account_id ? (
                <p className={ENVELOPE_CLASSES.err}>{errors.from_account_id.message}</p>
              ) : fromSpec?.helperText ? (
                <p className={ENVELOPE_CLASSES.help}>{fromSpec.helperText}</p>
              ) : null}
            </div>
          }
        />

        {/* (5a) Causale. */}
        <VendorPreviewField
          label={i18n._(msg`Causale`)}
          wide
          value={
            <div className="flex flex-col gap-1">
              <input
                type="text"
                className={ENVELOPE_CLASSES.input}
                value={descValue}
                onChange={(e) => commit("description", e.target.value)}
                disabled={disabled || descSpec?.readonly}
                maxLength={descSpec?.max ?? 140}
                placeholder={i18n._(msg`Causale del bonifico (es. Fattura 2026/123)`)}
                aria-label={i18n._(msg`Causale`)}
              />
              {errors?.description ? (
                <p className={ENVELOPE_CLASSES.err}>{errors.description.message}</p>
              ) : descSpec?.helperText ? (
                <p className={ENVELOPE_CLASSES.help}>{descSpec.helperText}</p>
              ) : null}
            </div>
          }
        />

        {/* (5b) Instant toggle. */}
        {showInstant ? (
          <VendorPreviewField
            label={i18n._(msg`Istantaneo`)}
            wide
            value={
              <div className="flex flex-col gap-1">
                <label className="inline-flex cursor-pointer select-none items-center gap-2">
                  <Checkbox
                    checked={instantValue}
                    onChange={(e) => commit("instant", e.target.checked)}
                    disabled={disabled || instantSpec?.readonly}
                  />
                  <span className="inline-flex items-center gap-1 text-ink-2 text-sm">
                    <Zap className="h-3.5 w-3.5 text-ink-3" aria-hidden />
                    {i18n._(msg`Accredito in pochi secondi (SCT Inst)`)}
                  </span>
                </label>
                {instantSpec?.helperText ? (
                  <p className={ENVELOPE_CLASSES.help}>{instantSpec.helperText}</p>
                ) : null}
              </div>
            }
          />
        ) : null}
      </VendorPreviewShell>
    </div>
  );
};
SepaPaymentPreview.displayName = "SepaPaymentPreview";
